import type { ReactElement, ReactNode } from 'react';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  FieldsRow,
  HeaderActions,
  MetadataItemRoot,
  MetadataTitle,
  MetadataValue,
  PageHeaderContainer,
  PageHeaderRoot,
  PageHeaderTitle,
} from './PageHeader.styled';

const DEFAULT_NAME_COL_SIZE = 2.5;
const DEFAULT_FIELDS_COL_SIZE = 8.5;
const DEFAULT_ACTIONS_COL_SIZE = 1;

export type PageHeaderField = {
  title: string;
  value?: string | null;
};

export type PageHeaderProps = {
  headerText: string;
  /** Metadata columns (label + value). When set, renders the details header layout. */
  fields?: PageHeaderField[];
  /** When true, renders the actions slot. Defaults to false. */
  showActions?: boolean;
  /** Action area content (one or more buttons). */
  actions?: ReactNode;
  fullWidth?: boolean;
  /** Grid size for the name column (default 2.5). */
  nameColSize?: number;
  /** Grid size for the fields column (default 8.5). */
  fieldsColSize?: number;
  /** Grid size for the actions column (default 1). */
  actionsColSize?: number;
  dataTestID?: string;
};

function PageHeaderFieldItem({ field }: Readonly<{ field: PageHeaderField }>): ReactElement {
  return (
    <MetadataItemRoot data-testid="page-header-field">
      <MetadataTitle>{field.title}</MetadataTitle>
      <MetadataValue>{field.value ?? ''}</MetadataValue>
    </MetadataItemRoot>
  );
}

/**
 * Page header: title only, or title + optional metadata fields and actions.
 */
export function PageHeader({
  headerText,
  fields,
  showActions = false,
  actions,
  fullWidth = true,
  nameColSize = DEFAULT_NAME_COL_SIZE,
  fieldsColSize = DEFAULT_FIELDS_COL_SIZE,
  actionsColSize = DEFAULT_ACTIONS_COL_SIZE,
  dataTestID = 'page-header-container',
}: Readonly<PageHeaderProps>): ReactElement {
  const hasDetailsLayout = (fields?.length ?? 0) > 0 || showActions;

  if (!hasDetailsLayout) {
    return (
      <PageHeaderContainer data-testid={dataTestID} fullWidth={fullWidth}>
        <Typography data-testid="page-header-text" variant="h5" component="h1">
          {headerText}
        </Typography>
      </PageHeaderContainer>
    );
  }

  const resolvedFieldsColSize = showActions ? fieldsColSize : fieldsColSize + actionsColSize;

  return (
    <PageHeaderContainer data-testid={dataTestID} fullWidth={fullWidth}>
      <PageHeaderRoot container>
        <Grid size={nameColSize}>
          <PageHeaderTitle component="h1" data-testid="page-header-text">
            <Tooltip title={headerText} placement="bottom" arrow>
              <span>{headerText}</span>
            </Tooltip>
          </PageHeaderTitle>
        </Grid>

        {(fields?.length ?? 0) > 0 ? (
          <Grid size={resolvedFieldsColSize}>
            <FieldsRow data-testid="page-header-fields">
              {fields!.map((field) => (
                <PageHeaderFieldItem key={`${field.title}-${field.value ?? ''}`} field={field} />
              ))}
            </FieldsRow>
          </Grid>
        ) : null}

        {showActions ? (
          <Grid size={actionsColSize}>
            <HeaderActions>{actions}</HeaderActions>
          </Grid>
        ) : null}
      </PageHeaderRoot>
    </PageHeaderContainer>
  );
}

export default PageHeader;
